<script setup lang="ts">
/**
 * BranchRankingTable — merged 57-branch ranking table.
 *
 * Replaces the old sales-only ranking + the front-page region comparison table.
 * Shows the filtered branch set (sales + voice), 10 branches per page, with:
 *   - อันดับ: fixed sales-position badge (chain-wide MTD dense rank from the
 *     server — never renumbers under filtering; local dense rank only as a
 *     fallback, nulls pinned below)
 *   - สาขา (+ brand tag folded in)
 *   - ยอดขายตามช่วงที่เลือก (+ Δ% inline), ยอดเฉลี่ยต่อหัว, ส่วนลดโปรโมชัน
 *   - เสียงลูกค้า: composite ★ rating + รีวิวเชิงลบ % (AI chip) + staleness marker
 *
 * Filter-driven via props (region/brand/q/topic) — same filters that drive the
 * map. No internal search box. Tap row → /branches/:id.
 *
 * bead: lucky-war-t8n0 (A4) + lucky-war-96rh (A6 honesty markers)
 * merge design: see docs/plans/EXCEL-REPLACEMENT-ROADMAP.md §3.1.1
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import type { MetricWindowParams, RankingBranchSummary, PerHeadDay, SalesFreshness } from '@/api';
import { fetchBranchRanking, fetchSalesOverview } from '@/api';
import { MAX_COMPARE_BRANCHES } from '@/lib/compare';
import { stripBrandPrefix, fmtRating, fmtNegativePct, negThresholdClass } from '@/lib/display';
import { deltaSign, fmtDelta as fmtPctDelta } from '@/lib/weekly';
import { useBranchNavigation } from '@/composables/useBranchNavigation';
import BranchName from '@/components/BranchName.vue';

const props = defineProps<{
  items?: RankingBranchSummary[];
  region?: string;   // '_all' or a region id
  brand?: string;
  q?: string;
  topic?: string;
  windowLabel?: string;
  navigationWindow?: MetricWindowParams;
  // ── Compare selection (bead lucky-war-d9b7) ──
  // Opt-in: only /branch enables it; home keeps the plain table. Selection
  // state itself lives in the URL and is owned by the parent — the table
  // only renders checkboxes and emits toggles.
  selectable?: boolean;
  pickedIds?: string[];
}>();

const emit = defineEmits<{ (e: 'toggle-pick', id: string): void }>();

function isPicked(id: string): boolean {
  return props.pickedIds?.includes(id) ?? false;
}

// Honest cap: once MAX is picked, unchecked boxes disable (with an
// explanation) instead of silently dropping later picks.
function pickDisabled(id: string): boolean {
  return !isPicked(id) && (props.pickedIds?.length ?? 0) >= MAX_COMPARE_BRANCHES;
}

function pickControlLabel(id: string, name: string): string {
  if (isPicked(id)) return `ยกเลิกการเลือก ${name}`;
  if (pickDisabled(id)) {
    return `${name}: เลือกครบ ${MAX_COMPARE_BRANCHES} สาขาแล้ว หากต้องการเลือกสาขานี้ ให้ยกเลิกการเลือกสาขาเดิมก่อน`;
  }
  return `เลือก ${name} เพื่อเปรียบเทียบ`;
}

function onTogglePick(id: string): void {
  if (pickDisabled(id)) return;
  emit('toggle-pick', id);
}

const router = useRouter();
const { branchLocation } = useBranchNavigation();

const data = ref<RankingBranchSummary[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

// Hover help text (mgmt request 2026-08-26). Definitions mirror the server:
// per_head = net_total / cust_count of the latest day with data
// (per_head_trend_14d last point, branches.go); promo_cost_ratio_7d =
// SUM(discount) / SUM(gross_sales) over the inclusive trailing-7d ICT
// window (sales_window.go).
const HINT_PER_HEAD = 'ยอดขายเฉลี่ยต่อหัว = ยอดขาย (ไม่รวม VAT) ÷ จำนวนลูกค้าในวันล่าสุดที่มีข้อมูล';
const HINT_PROMO_COST = 'สัดส่วนส่วนลดโปรโมชัน = มูลค่าส่วนลดรวม ÷ ยอดขายก่อนหักส่วนลด ย้อนหลัง 7 วัน — ค่าสูงหมายถึงส่วนลดสูงเมื่อเทียบกับยอดขายก่อนหักส่วนลด';
const PAGE_SIZE = 10;
const currentPage = ref(1);

// Sales MTD freshness stamp (mgmt feedback 2026-08-13). Best-effort: the
// ranking endpoint itself has no freshness field, so we read it from the MTD
// sales overview. Failure leaves the stamp blank rather than blocking the table.
const salesFreshness = ref<SalesFreshness | null>(null);
const salesWindowLabel = computed(() => props.windowLabel ?? '30 วัน');

async function loadFreshness() {
  try {
    const res = await fetchSalesOverview({ range: 'mtd', brand: props.brand });
    salesFreshness.value = res.freshness ?? null;
  } catch {
    salesFreshness.value = null;
  }
}
if (props.items === undefined) loadFreshness();

// ── Helpers to flatten nested server shapes into sortable/templatable values ──
function latestPerHead(b: RankingBranchSummary): number | null {
  const valid = b.per_head_trend_14d?.filter((d: PerHeadDay) => d.per_head != null);
  return valid?.length ? valid[valid.length - 1]!.per_head : null;
}

function perHeadChainAvg(b: RankingBranchSummary): number | null {
  const valid = b.per_head_trend_14d?.filter((d: PerHeadDay) => d.per_head != null);
  return valid?.length ? valid[valid.length - 1]!.chain_avg : null;
}

function promoCostValue(b: RankingBranchSummary): number | null {
  return b.promo_cost_ratio_7d?.value ?? null;
}

function promoCostChainAvg(b: RankingBranchSummary): number | null {
  return b.promo_cost_ratio_7d?.chain_avg ?? null;
}

function salesValue(b: RankingBranchSummary): number | null {
  return b.window_sales === undefined || b.window_sales === null
    ? b.mtd_net_total
    : b.window_sales.net_total;
}

function salesWowValue(b: RankingBranchSummary): number | null {
  return b.window_sales?.comparison_available ? b.window_sales.wow_delta_pct : null;
}

function salesWowClass(b: RankingBranchSummary): string {
  const value = salesWowValue(b);
  if (value == null) return '';
  return value > 0 ? 'branch-rank__delta--up' : value < 0 ? 'branch-rank__delta--down' : '';
}

function fmtSalesWow(b: RankingBranchSummary): string {
  const value = salesWowValue(b);
  if (value == null) return '—';
  const points = value * 100;
  return `${deltaSign(points)} ${fmtPctDelta(points)}%`;
}

// ── Voice helpers ─────────────────────────────────────────────────────────────
const STALE_DAYS = 14;
function isStale(b: RankingBranchSummary): boolean {
  if (!b.last_success_at) return false;
  const then = new Date(b.last_success_at).getTime();
  if (Number.isNaN(then)) return false;
  return Date.now() - then > STALE_DAYS * 24 * 60 * 60 * 1000;
}

// ── Sort state ────────────────────────────────────────────────────────────────
// Note: 'rank' is NOT sortable — it's a fixed sales-position badge (design §2).
// Today's-sales column removed (mgmt feedback 2026-08-13) — rank + default sort
// are now month-to-date.
type SortCol = 'name' | 'mtd' | 'per_head' | 'promo_cost' | 'rating' | 'negative';
const sortCol = ref<SortCol>('mtd');
const sortDir = ref<-1 | 1>(-1); // -1 = descending; mtd desc = rank order

function toggleSort(col: SortCol) {
  currentPage.value = 1;
  if (col === 'name') {
    // name sorts ascending by default when first clicked
    if (sortCol.value === col) {
      sortDir.value = (sortDir.value * -1) as -1 | 1;
    } else {
      sortCol.value = col;
      sortDir.value = 1;
    }
    return;
  }
  if (sortCol.value === col) {
    sortDir.value = (sortDir.value * -1) as -1 | 1;
  } else {
    sortCol.value = col;
    // negative sentiment is worst-when-high → default desc; others default desc too for consistency
    sortDir.value = -1;
  }
}

function sortLabel(col: SortCol): string {
  if (sortCol.value !== col) return '';
  return sortDir.value === -1 ? ' ↓' : ' ↑';
}

// ── Filtering + sorting (driven by props, no internal search) ─────────────────
const filtered = computed(() => {
  const items = data.value.slice();
  items.sort((a, b) => {
    let va: number | string = 0;
    let vb: number | string = 0;
    switch (sortCol.value) {
      case 'name':
        va = stripBrandPrefix(a.name_th);
        vb = stripBrandPrefix(b.name_th);
        return sortDir.value * va.localeCompare(vb);
      case 'mtd':
        va = salesValue(a) ?? -Infinity;
        vb = salesValue(b) ?? -Infinity;
        break;
      case 'per_head':
        va = latestPerHead(a) ?? -Infinity;
        vb = latestPerHead(b) ?? -Infinity;
        break;
      case 'promo_cost':
        va = promoCostValue(a) ?? -Infinity;
        vb = promoCostValue(b) ?? -Infinity;
        break;
      case 'rating':
        va = a.rating ?? -Infinity;
        vb = b.rating ?? -Infinity;
        break;
      case 'negative':
        // nulls last regardless of dir
        va = a.negative_pct ?? -Infinity;
        vb = b.negative_pct ?? -Infinity;
        break;
      default:
        break;
    }
    return sortDir.value * ((va as number) - (vb as number));
  });
  return items;
});

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / PAGE_SIZE)));
const pageStart = computed(() => (currentPage.value - 1) * PAGE_SIZE);
const paginated = computed(() => filtered.value.slice(pageStart.value, pageStart.value + PAGE_SIZE));

watch(totalPages, (pages) => {
  if (currentPage.value > pages) currentPage.value = pages;
});

function setPage(page: number) {
  currentPage.value = Math.min(Math.max(page, 1), totalPages.value);
}

// ── Fixed sales-position rank (design §2) ─────────────────────────────────────
// Rank is the branch's chain-wide position by MTD net_total (mgmt feedback
// 2026-08-13: rank by month-to-date). The server computes mtd_rank over ALL
// active branches independent of filters, so the badge keeps the branch's
// real rank when the list is filtered (e.g. a search must not renumber the
// survivors #1, #2, …). The local dense rank below is only a FALLBACK for
// rows carrying no mtd_rank (DEV mock / stale cached responses); it ranks
// the currently-loaded set. Null MTD → no rank ('—'). Stable under re-sort.
const rankMap = computed<Map<string, number>>(() => {
  const withSales = data.value
    .filter((b) => b.mtd_net_total != null)
    .slice()
    .sort((a, b) => (b.mtd_net_total as number) - (a.mtd_net_total as number));
  const m = new Map<string, number>();
  let rank = 0;
  let prev: number | null = null;
  for (const b of withSales) {
    const v = b.mtd_net_total as number;
    if (prev === null || v !== prev) {
      rank += 1;
      prev = v;
    }
    m.set(b.id, rank);
  }
  return m;
});

function rankOf(b: RankingBranchSummary): string {
  if (b.mtd_rank != null) return String(b.mtd_rank);
  const r = rankMap.value.get(b.id);
  return r == null ? '—' : String(r);
}

// ── Average row (client-side benchmark, design §5) ────────────────────────────
function meanNonNull(vals: (number | null)[]): number | null {
  const valid = vals.filter((v): v is number => v != null && !Number.isNaN(v));
  if (!valid.length) return null;
  return valid.reduce((s, v) => s + v, 0) / valid.length;
}

const avgRow = computed(() => {
  const items = data.value;
  if (!items.length) return null;
  // per-head chain avg: take the latest non-null chain_avg available across branches
  const phChainAvgs = items
    .map((b) => perHeadChainAvg(b))
    .filter((v): v is number => v != null);
  const promoChainAvgs = items
    .map((b) => promoCostChainAvg(b))
    .filter((v): v is number => v != null);
  return {
    mtd: meanNonNull(items.map((b) => salesValue(b))),
    per_head: phChainAvgs.length ? phChainAvgs[phChainAvgs.length - 1]! : null,
    promo_cost: promoChainAvgs.length ? promoChainAvgs[promoChainAvgs.length - 1]! : null,
    rating: meanNonNull(items.map((b) => b.rating)),
    negative_pct: meanNonNull(items.map((b) => b.negative_pct)),
  };
});

function avgLabel(): string {
  if (!props.region || props.region === '_all') return 'เฉลี่ยทุกสาขา';

  // Keep the region ID in the request only. The branch response already
  // carries the display name for the selected cohort.
  for (const branch of data.value) {
    const regionName = branch.region?.name_th?.trim();
    if (regionName) return `เฉลี่ย ${regionName}`;
  }
  return 'เฉลี่ยภูมิภาค';
}

// ── Load (prop-driven, abortable) ─────────────────────────────────────────────
let abortCtrl: AbortController | null = null;
let reloadTimer: ReturnType<typeof setTimeout> | null = null;

async function load() {
  if (props.items !== undefined) return;
  abortCtrl?.abort();
  const controller = new AbortController();
  abortCtrl = controller;
  loading.value = true;
  error.value = null;
  try {
    const branches = await fetchBranchRanking(
      { region: props.region, brand: props.brand, q: props.q, topic: props.topic },
      controller.signal,
    );
    if (controller.signal.aborted) return;
    if (branches && Array.isArray(branches) && branches.length > 0) {
      data.value = branches;
      loading.value = false;
      return;
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return;
    // fall through to error / mock path
  }

  // ── Fallback (DEV only — real endpoint unavailable) ──
  console.warn('[MOCK FALLBACK] BranchRankingTable — real endpoint failed, using mock');
  if (import.meta.env.PROD) {
    error.value = 'ไม่สามารถโหลดข้อมูลสาขาได้';
    loading.value = false;
    return;
  }
  const brands = ['suki', 'bbq'];
  const regions = ['BKK Central', 'BKK East', 'BKK North', 'North', 'South', 'East', 'Northeast'];
  const mock: RankingBranchSummary[] = [];
  for (let i = 1; i <= 57; i++) {
    const b = brands[i % 2];
    const hasSales = i % 3 !== 0; // ~2/3 of branches have sales
    mock.push({
      id: `br${String(i).padStart(3, '0')}`,
      name_th: `${b === 'suki' ? 'Lucky Suki ' : 'Lucky BBQ '}สาขา ${i}`,
      address_th: '', phone: null, lat: null, lng: null,
      region: { name_th: regions[i % regions.length] },
      brand: { name_th: b === 'suki' ? 'Lucky Suki' : 'Lucky BBQ', code: b },
      rating: 3.5 + Math.random() * 1.5,
      review_count: Math.floor(50 + Math.random() * 200),
      reviews_in_db: Math.floor(40 + Math.random() * 180),
      latest_review_at: null,
      negative_pct: Math.random() * 15,
      negative_enriched: null,
      sales_net_total: hasSales ? 8000 + Math.floor(Math.random() * 60000) : null,
      health_status: 'ok', last_success_at: null,
      brand_id: b, brand_name_th: b === 'suki' ? 'Lucky Suki' : 'Lucky BBQ',
      per_head_trend_14d: hasSales ? Array.from({ length: 8 }, (_, d) => ({
        date: `2026-08-${String(d + 3).padStart(2, '0')}`,
        per_head: 180 + Math.random() * 120,
        chain_avg: 215,
      })) : [],
      promo_cost_ratio_7d: hasSales ? { value: Math.random() * 0.2, chain_avg: 0.08 } : { value: 0, chain_avg: 0.08 },
      mtd_net_total: hasSales ? 80000 + Math.floor(Math.random() * 600000) : null,
      dod_delta_pct: hasSales ? (Math.random() - 0.5) * 30 : null,
    } as RankingBranchSummary);
  }
  data.value = mock;
  loading.value = false;
}

// Reload (debounced) when any filter prop changes. Mirrors HomeView's search debounce.
watch(
  () => [props.region, props.brand, props.q, props.topic],
  () => {
    if (props.items !== undefined) return;
    currentPage.value = 1;
    if (reloadTimer) clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => load(), 300);
  },
);

watch(() => props.items, (items) => {
  if (items === undefined) return;
  data.value = items;
  currentPage.value = 1;
  loading.value = false;
  error.value = null;
}, { immediate: true });

onMounted(() => {
  if (props.items === undefined) load();
});
onUnmounted(() => {
  abortCtrl?.abort();
  if (reloadTimer) clearTimeout(reloadTimer);
});

function goBranch(id: string) {
  if (!props.navigationWindow) {
    void router.push(branchLocation(id));
    return;
  }

  const query: Record<string, string> = {};
  if (props.navigationWindow.days) query.days = String(props.navigationWindow.days);
  if (props.navigationWindow.from) query.from = props.navigationWindow.from;
  if (props.navigationWindow.to) query.to = props.navigationWindow.to;
  void router.push({ name: 'branch', params: { id }, query });
}

// ── Formatters ────────────────────────────────────────────────────────────────
function fmtThaiDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(d);
}
function fmtDateShort(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short' }).format(d);
}
function fmtFreshnessStamp(): string {
  const f = salesFreshness.value;
  if (!f) return '';
  const parts: string[] = [];
  if (f.data_through) parts.push(`ข้อมูลถึง ${fmtDateShort(f.data_through)}`);
  if (f.last_ingest_at) parts.push(`อัปเดตล่าสุด ${fmtThaiDateTime(f.last_ingest_at)}`);
  return parts.join(' · ');
}

function fmtBaht(n: number | null | undefined): string {
  if (n == null) return '—';
  return Math.round(n).toLocaleString('th-TH');
}

function fmtPctNoSign(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function fmtDelta(val: number | null, chainAvg: number | null): string {
  if (val == null || chainAvg == null) return '—';
  const delta = val - chainAvg;
  if (Math.abs(delta) < 0.5) return '≈ ค่าเฉลี่ย';
  return delta > 0 ? `+฿${fmtBaht(delta)}` : `-฿${fmtBaht(Math.abs(delta))}`;
}

function fmtDeltaClass(val: number | null, chainAvg: number | null): string {
  if (val == null || chainAvg == null) return '';
  const delta = val - chainAvg;
  if (Math.abs(delta) < 0.5) return '';
  return delta > 0 ? 'branch-rank__delta--up' : 'branch-rank__delta--down';
}

function negClass(p: number | null): string {
  // Canonical 5/15 threshold (negThresholdClass) — keeps the ranking table's
  // negative-% colour consistent with TriageCard / ComplaintTopicCard.
  const t = negThresholdClass(p);
  return t ? `branch-rank__neg--${t}` : '';
}

</script>

<template>
  <section class="branch-rank" :class="{ 'branch-rank--selectable': selectable }" aria-label="จัดอันดับสาขา">
    <!-- Loading -->
    <div v-if="loading" class="state-msg">
      <p>กำลังโหลดข้อมูลสาขา...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="state-msg">
      <p>ไม่สามารถโหลดข้อมูลสาขาได้</p>
      <p class="branch-rank__error-detail">{{ error }}</p>
      <button class="pager__btn" @click="load()">ลองใหม่</button>
    </div>

    <!-- Data -->
    <template v-else-if="data.length > 0">
      <h2 class="branch-rank__header">
        <span>จัดอันดับสาขา ({{ data.length }} สาขา)</span>
      </h2>

      <!-- Honesty chips + freshness stamp (mgmt feedback 2026-08-13: MTD) -->
      <div
        v-if="data.length > 0"
        class="branch-rank__honesty"
        aria-label="หมายเหตุทางสถิติ"
      >
        <span class="branch-rank__honesty-chip">
          ยอดขาย {{ salesWindowLabel }}: มีข้อมูล {{ data.filter(b => salesValue(b) != null).length }} สาขา
        </span>
        <span class="branch-rank__honesty-chip">
          รีวิวเชิงลบ (%) วิเคราะห์ข้อความด้วย AI
        </span>
        <span v-if="fmtFreshnessStamp()" class="branch-rank__honesty-chip">
          {{ fmtFreshnessStamp() }}
        </span>
      </div>

      <!-- Zero-selection affordance: keeps comparison discoverable without
           showing a sticky tray containing a disabled primary action. Copy
           reviewed by communication-excellence-coach (lucky-war-kluz). -->
      <div v-if="selectable && !(pickedIds?.length)" class="branch-rank__compare-prompt">
        <strong>เปรียบเทียบสาขา (สูงสุด {{ MAX_COMPARE_BRANCHES }} สาขา)</strong>
        <span>เลือก 2–6 สาขาเพื่อเปรียบเทียบ</span>
      </div>

      <!-- Average row (benchmark) -->
      <div v-if="avgRow" class="branch-rank__avg" aria-label="ค่าเฉลี่ยเปรียบเทียบ">
        <span class="branch-rank__avg-rank">—</span>
        <span class="branch-rank__avg-name">{{ avgLabel() }}</span>
        <div class="branch-rank__avg-kpis">
          <span class="branch-rank__avg-kpi">
            <span class="branch-rank__avg-kpi-label">ยอดขาย {{ salesWindowLabel }}</span>
            <span class="branch-rank__avg-kpi-val">฿{{ fmtBaht(avgRow.mtd) }}</span>
          </span>
          <span class="branch-rank__avg-kpi">
            <span class="branch-rank__avg-kpi-label" :title="HINT_PER_HEAD">ยอดเฉลี่ยต่อหัว</span>
            <span class="branch-rank__avg-kpi-val">฿{{ fmtBaht(avgRow.per_head) }}</span>
          </span>
          <span class="branch-rank__avg-kpi">
            <span class="branch-rank__avg-kpi-label" :title="HINT_PROMO_COST">ส่วนลดโปรโมชัน</span>
            <span class="branch-rank__avg-kpi-val">{{ fmtPctNoSign(avgRow.promo_cost) }}</span>
          </span>
          <span class="branch-rank__avg-kpi branch-rank__avg-kpi--voice">
            <span class="branch-rank__avg-kpi-label">เสียงลูกค้า</span>
            <span class="branch-rank__avg-kpi-val">
              ★ {{ fmtRating(avgRow.rating) }}
              <span class="branch-rank__neg">รีวิวเชิงลบ {{ fmtNegativePct(avgRow.negative_pct) }}</span>
            </span>
          </span>
        </div>
      </div>

      <!-- Column headers (hidden on mobile) -->
      <div class="branch-rank__cols">
        <span v-if="selectable" class="branch-rank__col branch-rank__col--pick" aria-hidden="true"></span>
        <span class="branch-rank__col branch-rank__col--rank">อันดับ</span>
        <button class="branch-rank__col branch-rank__col--name" @click="toggleSort('name')">
          สาขา{{ sortLabel('name') }}
        </button>
        <button class="branch-rank__col" @click="toggleSort('mtd')">
          ยอดขาย {{ salesWindowLabel }}{{ sortLabel('mtd') }}
        </button>
        <button class="branch-rank__col" :title="HINT_PER_HEAD" @click="toggleSort('per_head')">
          ยอดเฉลี่ยต่อหัว{{ sortLabel('per_head') }}
        </button>
        <button class="branch-rank__col" :title="HINT_PROMO_COST" @click="toggleSort('promo_cost')">
          ส่วนลดโปรโมชัน{{ sortLabel('promo_cost') }}
        </button>
        <button class="branch-rank__col branch-rank__col--voice" @click="toggleSort('rating')">
          เสียงลูกค้า{{ sortLabel('rating') }}
        </button>
      </div>

      <!-- Row list — mobile: cards; desktop: table rows -->
      <div class="branch-rank__list">
        <!-- rowwrap is display:contents when not selectable, so the home
             table's DOM/layout is unchanged; selectable mode turns it into a
             grid pairing a checkbox strip with the row button (checkbox must
             live OUTSIDE the row <button> — interactive content cannot nest,
             and a sibling can never fire the row's navigation). -->
        <div
          v-for="b in paginated"
          :key="b.id"
          class="branch-rank__rowwrap"
          :class="{ 'branch-rank__rowwrap--pick': selectable }"
        >
          <label
            v-if="selectable"
            class="branch-rank__pick"
            :class="{ 'branch-rank__pick--on': isPicked(b.id) }"
            :title="pickControlLabel(b.id, b.name_th)"
          >
            <input
              type="checkbox"
              class="branch-rank__pick-input"
              :checked="isPicked(b.id)"
              :disabled="pickDisabled(b.id)"
              :aria-label="pickControlLabel(b.id, b.name_th)"
              @change="onTogglePick(b.id)"
            />
          </label>
          <button
            class="branch-rank__row"
            @click="goBranch(b.id)"
          >
          <!-- Mobile card layout -->
          <div class="branch-rank__identity">
            <span class="branch-rank__row-rank" :class="{ 'branch-rank__row-rank--na': rankOf(b) === '—' }">#{{ rankOf(b) }}</span>
            <div class="branch-rank__row-top">
              <span class="branch-rank__row-name"><BranchName :code="b.brand?.code || b.brand_id" :name="b.name_th" /></span>
            </div>
          </div>
          <div class="branch-rank__row-kpis">
            <div class="branch-rank__row-kpi">
              <span class="branch-rank__row-kpi-label">ยอดขาย {{ salesWindowLabel }}</span>
              <span class="branch-rank__row-kpi-val">
                ฿{{ fmtBaht(salesValue(b)) }}
                <span
                  v-if="b.window_sales?.comparison_available"
                  class="branch-rank__row-kpi-delta"
                  :class="salesWowClass(b)"
                  title="เปลี่ยนแปลงเทียบช่วงก่อนหน้า"
                >{{ fmtSalesWow(b) }}</span>
              </span>
            </div>
            <div class="branch-rank__row-kpi">
              <span class="branch-rank__row-kpi-label" :title="HINT_PER_HEAD">ยอดเฉลี่ยต่อหัว</span>
              <span class="branch-rank__row-kpi-val">
                ฿{{ fmtBaht(latestPerHead(b)) }}
                <span
                  class="branch-rank__row-kpi-delta"
                  :class="fmtDeltaClass(latestPerHead(b), perHeadChainAvg(b))"
                >{{ fmtDelta(latestPerHead(b), perHeadChainAvg(b)) }}</span>
              </span>
            </div>
            <div class="branch-rank__row-kpi">
              <span class="branch-rank__row-kpi-label" :title="HINT_PROMO_COST">ส่วนลดโปรโมชัน</span>
              <span class="branch-rank__row-kpi-val">
                {{ fmtPctNoSign(promoCostValue(b)) }}
                <span
                  v-if="promoCostChainAvg(b) != null && promoCostValue(b) != null"
                  class="branch-rank__row-kpi-delta"
                  :class="(promoCostValue(b)! - promoCostChainAvg(b)!) > 0.01 ? 'branch-rank__delta--down' : ''"
                >
                  {{ (promoCostValue(b)! - promoCostChainAvg(b)!) > 0.01 ? '↑' : '' }}
                </span>
              </span>
            </div>
            <!-- Voice composite cell -->
            <div class="branch-rank__row-kpi branch-rank__row-kpi--voice">
              <span class="branch-rank__row-kpi-label">เสียงลูกค้า</span>
              <span class="branch-rank__row-kpi-val branch-rank__voice">
                <span class="branch-rank__voice-rating">
                  <span class="branch-rank__star">★</span>{{ fmtRating(b.rating) }}
                </span>
                <span
                  v-if="b.negative_pct != null"
                  class="branch-rank__voice-neg"
                  :class="negClass(b.negative_pct)"
                >
                  รีวิวเชิงลบ {{ fmtNegativePct(b.negative_pct) }}
                  <span class="branch-rank__exp-chip" title="ข้อมูลนี้มาจากการวิเคราะห์ของ AI (LLM) — ใช้ประกอบการตัดสินใจเท่านั้น">AI</span>
                </span>
                <span v-else-if="b.reviews_in_db > 0" class="branch-rank__voice-na">ข้อมูลรีวิวที่วิเคราะห์ด้วย AI ไม่เพียงพอสำหรับคำนวณ</span>
                <span v-else class="branch-rank__voice-na">ยังไม่มีรีวิว</span>
                <span v-if="isStale(b)" class="branch-rank__stale-chip" title="ไม่ได้อัปเดตเกิน 14 วัน">ข้อมูลเก่า</span>
              </span>
            </div>
          </div>
        </button>
        </div>

        <!-- Empty -->
        <div v-if="filtered.length === 0" class="state-msg">
          <p>ไม่พบสาขาที่ตรงกับตัวกรอง</p>
        </div>
      </div>

      <nav v-if="filtered.length > PAGE_SIZE" class="branch-rank__pager" aria-label="หน้ารายชื่อสาขา">
        <span class="branch-rank__pager-summary">
          แสดง {{ pageStart + 1 }}–{{ Math.min(pageStart + PAGE_SIZE, filtered.length) }} จาก {{ filtered.length }} สาขา
        </span>
        <div class="branch-rank__pager-actions">
          <button class="pager__btn" :disabled="currentPage === 1" @click="setPage(currentPage - 1)">ก่อนหน้า</button>
          <span class="branch-rank__pager-page" aria-current="page">หน้า {{ currentPage }} จาก {{ totalPages }}</span>
          <button class="pager__btn" :disabled="currentPage === totalPages" @click="setPage(currentPage + 1)">ถัดไป</button>
        </div>
      </nav>

      <!-- Source note (transparency: rating = Google's published value) -->
      <p class="branch-rank__source-note">
        ยอดขายไม่รวม VAT · คะแนนจาก Google · รีวิวเชิงลบ (%) วิเคราะห์ข้อความด้วย AI และแสดงเมื่อมีข้อมูลเพียงพอ · ป้ายอันดับเป็นอันดับยอดขายสะสมตั้งแต่ต้นเดือน (เฉพาะสาขาที่มีข้อมูลยอดขาย)
      </p>
    </template>
  </section>
</template>

<style scoped>
.branch-rank {
  /* Keep the desktop header and row columns defined in one place. */
  /* per-head + promo columns widened for the fuller Thai labels (2026-08-26) */
  --branch-rank-columns: 2rem minmax(0, 3fr) 1.5fr 1.8fr 1.6fr 1.8fr;
  margin-bottom: 1rem;
}

.branch-rank__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font: var(--text-card-label);
  font-weight: 600;
  color: var(--text-dim);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.25rem;
  font-family: var(--font-display);
}

/* ── Honesty chips (A6) ──────────────────────────────────────────────────── */
.branch-rank__honesty {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
}

.branch-rank__honesty-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.45rem;
  font: var(--text-caption);
  font-weight: 600;
  background: var(--surface-2);
  color: var(--text-muted);
  border-radius: var(--radius-badge);
  border: 1px solid var(--line-light);
  font-family: var(--font-body);
}

.branch-rank__compare-prompt {
  display: flex;
  align-items: baseline;
  gap: 0.3rem 0.65rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
  padding: 0.5rem 0.65rem;
  border: 1px dashed var(--brand-light);
  border-radius: var(--radius-badge);
  background: var(--brand-soft);
}

.branch-rank__compare-prompt strong {
  color: var(--text);
  font: 600 0.78rem 'Kanit', sans-serif;
}

.branch-rank__compare-prompt span {
  color: var(--text-dim);
  font: 500 0.7rem 'Sarabun', sans-serif;
}

/* ── Average row ─────────────────────────────────────────────────────────── */
.branch-rank__avg {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-badge);
  padding: 0.5rem 0.65rem;
  margin-bottom: 0.5rem;
}

.branch-rank__avg-rank {
  font: var(--text-caption);
  font-weight: 700;
  color: var(--text-muted);
  min-width: 1.8rem;
  text-align: center;
}

.branch-rank__avg-name {
  font: var(--text-card-label);
  font-weight: 700;
  color: var(--brand-light);
  font-family: var(--font-display);
  flex: 1;
  min-width: 0;
}

.branch-rank__avg-kpis {
  display: contents;
}

.branch-rank__avg-kpi {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
}

.branch-rank__avg-kpi-label {
  font-size: 0.55rem; /* n9vn.2: off-scale — dense table label */
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-family: var(--font-display);
}

.branch-rank__avg-kpi-val {
  font: var(--text-body-sm);
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: var(--brand-light);
  font-family: var(--font-body);
}

.branch-rank__avg-kpi--voice .branch-rank__avg-kpi-val {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  flex-wrap: wrap;
}

.branch-rank__neg { color: var(--degraded); font: var(--text-source); }

/* ── Column headers (desktop) ────────────────────────────────────────────── */
.branch-rank__cols {
  display: none;
  grid-template-columns: var(--branch-rank-columns);
  gap: 0.3rem;
  margin-bottom: 0.3rem;
  padding: 0 0.5rem;
}

.branch-rank__col {
  background: none;
  border: none;
  color: var(--text-muted);
  font: var(--text-caption);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-family: var(--font-display);
  cursor: pointer;
  padding: 0.25rem 0.15rem;
  white-space: nowrap;
  text-align: left;
}

.branch-rank__col:hover { color: var(--text-dim); }

.branch-rank__col--rank { cursor: default; min-width: 0; text-align: center; }
.branch-rank__col--rank:hover { color: var(--text-muted); }
.branch-rank__col--name { min-width: 0; }

/* ── Row list ────────────────────────────────────────────────────────────── */
.branch-rank__list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

/* ── Compare-selection wrapper (bead lucky-war-d9b7) ─────────────────────── */
/* Transparent by default (home table unchanged); selectable mode pairs a
 * checkbox strip with the row button. The checkbox is a SIBLING of the row
 * button — interactive content cannot nest inside <button>, and a sibling
 * click can never fire the row's branch navigation. */
.branch-rank__rowwrap { display: contents; }

.branch-rank__rowwrap--pick {
  display: grid;
  grid-template-columns: 2.4rem minmax(0, 1fr);
  gap: 0.3rem;
  align-items: stretch;
}

.branch-rank__rowwrap--pick .branch-rank__row { width: auto; min-width: 0; }

.branch-rank__rowwrap--pick:has(:checked) .branch-rank__row {
  border-color: var(--brand-light);
}

.branch-rank__pick {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid var(--line);
  border-radius: var(--radius-badge);
  background: var(--surface-2);
  min-height: 2.75rem; /* ≥44px touch target on desktop rows */
}

.branch-rank__pick:hover { border-color: var(--brand-light); }
.branch-rank__pick--on { border-color: var(--brand-light); background: var(--brand-soft); }
.branch-rank__pick:has(.branch-rank__pick-input:disabled) { cursor: not-allowed; opacity: 0.55; }

.branch-rank__pick-input {
  width: 1.1rem;
  height: 1.1rem;
  accent-color: var(--brand);
  cursor: pointer;
}
.branch-rank__pick-input:disabled { cursor: not-allowed; }

.branch-rank__row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-badge);
  padding: 0.55rem 0.65rem;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  text-align: left;
  font: inherit;
  color: inherit;
  width: 100%;
}

.branch-rank__row:hover,
.branch-rank__row:focus-visible {
  border-color: var(--brand-light);
  box-shadow: 0 0 0 2px var(--brand-soft);
  outline: none;
}

.branch-rank__row-top {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.branch-rank__identity {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.branch-rank__row-rank {
  font: var(--text-caption);
  font-weight: 700;
  color: var(--brand-light);
  background: var(--brand-soft);
  border-radius: var(--radius-badge);
  padding: 0.05rem 0.35rem;
  min-width: 1.8rem;
  text-align: center;
  font-family: var(--font-display);
}

.branch-rank__row-rank--na {
  color: var(--text-muted);
  background: var(--surface-2);
}

.branch-rank__row-name {
  font: var(--text-card-title);
  font-weight: 600;
  color: var(--text);
  font-family: var(--font-display);
  flex: 1;
  min-width: 0;
}

/* ── KPI grid inside each card ───────────────────────────────────────────── */
.branch-rank__row-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.35rem;
}

.branch-rank__row-kpi {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
}

.branch-rank__row-kpi--voice {
  grid-column: span 2;
}

.branch-rank__row-kpi-label {
  font-size: 0.55rem; /* n9vn.2: off-scale — dense table label */
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-family: var(--font-display);
}

.branch-rank__row-kpi-val {
  font: var(--text-body-sm);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--text);
  font-family: var(--font-body);
  display: flex;
  align-items: baseline;
  gap: 0.2rem;
  flex-wrap: wrap;
}

.branch-rank__row-kpi-dod {
  font-size: 0.62rem; /* n9vn.2: off-scale — compact delta */
  font-weight: 600;
  font-family: var(--font-body);
}

.branch-rank__dod--up { color: var(--ok); }
.branch-rank__dod--down { color: var(--color-bad); }

.branch-rank__row-kpi-delta {
  font-size: 0.58rem; /* n9vn.2: off-scale — compact delta */
  font-weight: 500;
  color: var(--text-dim);
  font-family: var(--font-body);
}

.branch-rank__delta--up { color: var(--ok); }
.branch-rank__delta--down { color: var(--color-bad); }

/* ── Voice composite cell ────────────────────────────────────────────────── */
.branch-rank__voice {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  flex-wrap: wrap;
}

.branch-rank__voice-rating {
  font-weight: 700;
  color: var(--text);
}

.branch-rank__star { color: var(--star); font: var(--text-caption); }

.branch-rank__voice-neg {
  font: var(--text-source);
  font-weight: 600;
  color: var(--text-dim);
}

.branch-rank__neg--high { color: var(--color-bad); }
.branch-rank__neg--mid { color: var(--degraded); }

.branch-rank__voice-na {
  font: var(--text-source);
  color: var(--text-muted);
  font-style: italic;
  font-weight: 400;
}

.branch-rank__exp-chip {
  display: inline-flex;
  padding: 0.02rem 0.22rem;
  font-size: 0.52rem; /* n9vn.2: off-scale — compact status chip */
  font-weight: 700;
  background: var(--degraded-soft);
  color: var(--degraded);
  border-radius: var(--radius-bar);
  margin-left: 0.15rem;
  vertical-align: middle;
  cursor: help;
}

.branch-rank__stale-chip {
  display: inline-flex;
  padding: 0.02rem 0.25rem;
  font-size: 0.52rem; /* n9vn.2: off-scale — compact status chip */
  font-weight: 700;
  background: var(--surface-2);
  color: var(--text-muted);
  border: 1px solid var(--line-light);
  border-radius: var(--radius-bar);
  cursor: help;
}

/* ── Source note ─────────────────────────────────────────────────────────── */
.branch-rank__source-note {
  margin: 0.5rem 0.15rem 0;
  font: var(--text-caption);
  color: var(--text-muted);
  font-family: var(--font-body);
  line-height: 1.4;
}

.branch-rank__pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.75rem;
  font-family: var(--font-body);
}

.branch-rank__pager-summary,
.branch-rank__pager-page {
  color: var(--text-muted);
  font: var(--text-caption);
}

.branch-rank__pager-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.branch-rank__pager .pager__btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

/* ── Shared ─────────────────────────────────────────────────────────────── */
.state-msg {
  padding: 1rem 0.5rem;
  font-family: var(--font-body);
  color: var(--text-dim);
  font: var(--text-body-sm);
}
.branch-rank__error-detail { font: var(--text-body-sm); color: var(--text-muted); }

/* ── Desktop (min-width: 640px): row layout ──────────────────────────────── */
@media (min-width: 640px) {
  .branch-rank__cols {
    display: grid;
  }

  /* Selectable mode: headers + average row align with the checkbox strip. */
  .branch-rank--selectable .branch-rank__cols {
    grid-template-columns: 2.4rem var(--branch-rank-columns);
  }

  .branch-rank--selectable .branch-rank__avg {
    margin-left: calc(2.4rem + 0.3rem);
  }

  .branch-rank__avg {
    display: grid;
    grid-template-columns: var(--branch-rank-columns);
    gap: 0.3rem;
    padding: 0.45rem 0.5rem;
  }

  .branch-rank__avg-kpis {
    display: contents;
  }

  .branch-rank__avg-kpi {
    align-items: flex-end;
    min-width: 0;
  }

  .branch-rank__row {
    display: grid;
    grid-template-columns: var(--branch-rank-columns);
    align-items: center;
    padding: 0.45rem 0.5rem;
  }

  .branch-rank__row-top {
    min-width: 0;
    flex-wrap: nowrap;
  }

  .branch-rank__identity { display: contents; }

  .branch-rank__row-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .branch-rank__row-kpis {
    display: contents;
  }

  .branch-rank__row > .branch-rank__row-kpis .branch-rank__row-kpi {
    align-items: flex-end;
    min-width: 0;
  }

  .branch-rank__row-kpi--voice {
    grid-column: auto;
  }

  .branch-rank__row-kpi-label {
    display: none;
  }

  .branch-rank__row-kpi-val,
  .branch-rank__avg-kpi-val,
  .branch-rank__col:not(.branch-rank__col--name):not(.branch-rank__col--rank) {
    justify-content: flex-end;
    text-align: right;
  }
}

@media (max-width: 639px) {
  .branch-rank__pager {
    align-items: stretch;
    flex-direction: column;
  }

  .branch-rank__pager-actions {
    justify-content: space-between;
  }
}
</style>
